import React, { useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { Option } from 'constants/models'

import { withAppService, withAppServiceProps } from 'services/app'
import { withMedicationFormsService, withMedicationFormsServiceProps } from 'services/medicationForms'
import { withReviewsService, withReviewsServiceProps } from 'services/reviews'
import { withRouter, withRouterProps } from 'services/router'
import { withPaginationUtils, withPaginationUtilsProps } from 'services/utils/pagination'

import { RootState } from 'core/reducers'

import HistoryView from './HistoryView'

const REVIEW_GROUPS = {
  read: [
    'review.author',
    'membership',
    'carer',
    'review.signatures',
    'review.medicationForm',
    'reviewSignature',
    'reviewSignature.signature',
    'reviewSignature.owner',
    'reviewSignature.carer',
    'reviewSignature.medicationForm',
    'medium',
    'medicationForm',
    'medicationForm.child',
    'child',
    'child.keyWorkers',
  ],
}

type HistoryContainerProps =
  & withAppServiceProps
  & withRouterProps
  & withMedicationFormsServiceProps
  & withReviewsServiceProps
  & withPaginationUtilsProps

const mapState = (state: RootState, {
  appSelectors,
  reviewsSelectors,
}: HistoryContainerProps) => ({
  isFetching: appSelectors.getIsFetching(state.medicationForms.single, state.reviews.list),
  reviewHistoryData: reviewsSelectors.getReviewHistoryData(state),
  totalResults: appSelectors.getTotalResults(state.reviews.list),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

const HistoryContainer: React.FC<HistoryContainerProps & PropsFromRedux> = ({
  isFetching,
  medicationFormsActions,
  paginationUtils,
  params,
  reviewHistoryData,
  reviewsActions,
  reviewsSelectors,
  totalResults,
}) => {
  const [selectedStaff, setSelectedStaff] = useState<Option>()
  const { id: medicationFormId } = params || {}
  const { getPageCount, onPageChange, page, setPageLocationQuery } = paginationUtils

  const pageCount = getPageCount(totalResults)

  useEffect(() => {
    setPageLocationQuery(false)
  }, [])

  useEffect(() => {
    medicationFormsActions.get({
      params: [medicationFormId, {}],
    })

    reviewsActions.list({
      mergeResult: 1 !== page,
      params: [{
        criteria: reviewsSelectors.getListCriteria({
          medicationFormId,
          membershipId: selectedStaff?.value as number,
        }),
        groups: REVIEW_GROUPS,
        page,
      }],
    })
  }, [page, selectedStaff])

  const handlePageChange = (newPage) => {
    onPageChange()(newPage)
  }

  const handleStaffChange = (selectedOption) => {
    setSelectedStaff(selectedOption)
  }

  return (
    <HistoryView
      isFetching={isFetching}
      page={page}
      pageCount={pageCount}
      reviewHistoryData={reviewHistoryData}
      staff={selectedStaff}
      onPageChange={handlePageChange}
      onStaffChange={handleStaffChange}
    />
  )
}

const enhance = compose(
  withAppService,
  withPaginationUtils,
  withMedicationFormsService,
  withReviewsService,
  withRouter,
  connector,
)

export default enhance(HistoryContainer)
